/**
 * Case facts card on the Build tab (INTAKE01): the case kind, the offence window and the stated claims.
 *
 * These are officer inputs, not findings, and the card says so. A run reads them: a declared kind decides the case
 * kind, the offence start bounds what counts as the case's money in the subject's own wallets, and each stated claim
 * is checked against the traced data and printed beside it in the report.
 *
 * Read-only for everyone until `GET /cases/{id}/facts` says `can_edit` (owner, the owner's admin, a super admin or an
 * editor collaborator; ACC02). The SPA never infers rights; the server answers 403 to a write it refuses and the card
 * shows its detail. R8: the load effect owns a `cancelled` flag.
 */
import { useEffect, useState } from 'react';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import {
  caseFactsService, type CaseFacts, type CaseFactsRecord, type CaseKind, type StatedClaim,
  type StatedClaimKind, type StatedClaimSource,
} from '../../../services/api';

export const CASE_FACTS_RULE =
  'These are officer inputs, not findings: the platform never infers them. A declared kind decides the case kind; the offence start bounds what counts as the case\'s money in the subject\'s own wallets; each stated claim is checked against the traced data and printed beside it.';

export const CASE_FACTS_READ_ONLY =
  'Only the case owner, the owner\'s admin, a super admin or a collaborator with the editor role can change the case facts; you can read them.';

export const KIND_LABEL: Record<CaseKind, string> = {
  exploit: 'Exploit', fraud_inflow: 'Fraud inflow', extortion: 'Extortion', hawala_layering: 'Hawala layering',
  other: 'Other', unknown: 'Unknown',
};
const KINDS = Object.keys(KIND_LABEL) as CaseKind[];
const CLAIM_KINDS: StatedClaimKind[] = ['total', 'channel_total', 'duration', 'count'];
const SOURCES: StatedClaimSource[] = ['FIR', 'complaint', 'prior report', 'officer'];

const blankClaim = (): StatedClaim => ({ kind: 'total', value: 0, unit: '', source: 'officer' });

/** The API's detail when it has one, else a fixed line. */
export function factsErrorText(e: unknown, fallback: string): string {
  const detail = (e as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
  return typeof detail === 'string' && detail ? detail : fallback;
}

/** A claim as one line of text for the read-only rows. */
export function claimText(c: StatedClaim): string {
  const what = c.kind === 'channel_total' ? ` through ${c.channel || '?'}` : c.path ? ` (${c.path})` : '';
  const refs = c.kind === 'duration' && (c.from_ref || c.to_ref)
    ? `, from ${String(c.from_ref || '?').slice(0, 10)} to ${String(c.to_ref || '?').slice(0, 10)}` : '';
  return `${c.kind.replace('_', ' ')}: ${c.value.toLocaleString('en-US')} ${c.unit}${what}${refs}`;
}

/** The facts the form holds, cleaned for the PUT body (empty optional text dropped). */
export function factsBody(kind: CaseKind | '', start: string, end: string, claims: StatedClaim[]): CaseFacts {
  const clean = (v?: string | null) => (v && v.trim() ? v.trim() : undefined);
  return {
    case_kind: kind || null,
    offence_window: start ? { start, end: end || null } : null,
    stated_claims: claims.map((c) => ({
      kind: c.kind, value: Number(c.value) || 0, unit: c.unit.trim(), source: c.source,
      channel: clean(c.channel), path: clean(c.path), from_ref: clean(c.from_ref), to_ref: clean(c.to_ref),
      note: clean(c.note),
    })),
  };
}

export function CaseFactsCard({ caseId }: { caseId: string }) {
  const [rec, setRec] = useState<CaseFactsRecord | null>(null);
  const [state, setState] = useState<'loading' | 'ready' | 'error'>('loading');
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [kind, setKind] = useState<CaseKind | ''>('');
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [claims, setClaims] = useState<StatedClaim[]>([]);

  useEffect(() => {
    let cancelled = false;
    setState('loading'); setRec(null); setEditing(false); setError('');
    // Started inside a promise so a service that throws synchronously still lands in the error state.
    Promise.resolve().then(() => caseFactsService.get(caseId))
      .then((r) => { if (!cancelled) { setRec(r); setState('ready'); } })
      .catch(() => { if (!cancelled) setState('error'); });
    return () => { cancelled = true; };
  }, [caseId]);

  const canEdit = rec?.can_edit === true;
  const facts = rec?.facts || {};
  const stated = facts.stated_claims || [];

  const startEdit = () => {
    setKind((facts.case_kind as CaseKind) || '');
    setStart(facts.offence_window?.start || '');
    setEnd(facts.offence_window?.end || '');
    setClaims(stated.map((c) => ({ ...c })));
    setError(''); setEditing(true);
  };

  const setClaim = (i: number, patch: Partial<StatedClaim>) =>
    setClaims((cs) => cs.map((c, j) => (j === i ? { ...c, ...patch } : c)));

  const save = async () => {
    if (busy) return;
    setBusy(true); setError('');
    try {
      const out = await caseFactsService.put(caseId, factsBody(kind, start, end, claims));
      setRec(out); setEditing(false);
    } catch (e) {
      setError(factsErrorText(e, 'The case facts could not be saved.'));
    } finally { setBusy(false); }
  };

  const input = 'text-[11px] px-1.5 py-0.5 bg-background border border-input rounded focus:border-primary outline-none';

  return (
    <section data-testid="case-facts-card" className="mx-3 mt-3 rounded-lg border border-border bg-card p-3 text-xs">
      <div className="flex items-center gap-2">
        <h3 className="font-semibold text-sm">Case facts</h3>
        {rec && rec.version > 0 && (
          <span className="text-[10px] text-muted-foreground" data-testid="case-facts-version">
            version {rec.version}{rec.set_by_username ? ` · set by ${rec.set_by_username}` : ''}{rec.set_at ? ` · ${String(rec.set_at).slice(0, 16).replace('T', ' ')}` : ''}
          </span>
        )}
        {state === 'ready' && canEdit && !editing && (
          <button data-testid="case-facts-edit" onClick={startEdit}
            className="ml-auto flex items-center gap-1 px-2 py-0.5 rounded border border-border text-[11px] hover:bg-secondary/60">
            <Pencil size={11} /> Edit
          </button>
        )}
      </div>
      <p data-testid="case-facts-rule" className="mt-1 text-[10px] text-muted-foreground">{CASE_FACTS_RULE}</p>

      {state === 'loading' && <p className="mt-2 text-muted-foreground">Loading the case facts...</p>}
      {state === 'error' && <p className="mt-2 text-destructive" data-testid="case-facts-error">The case facts could not be loaded.</p>}

      {state === 'ready' && !editing && (
        <div className="mt-2 space-y-1" data-testid="case-facts-view">
          <div><span className="text-muted-foreground">Kind:</span> <span data-testid="case-facts-kind">{facts.case_kind ? KIND_LABEL[facts.case_kind as CaseKind] || facts.case_kind : 'not declared'}</span></div>
          <div><span className="text-muted-foreground">Offence window:</span> <span data-testid="case-facts-window">{facts.offence_window?.start
            ? `from ${facts.offence_window.start}${facts.offence_window.end ? ` to ${facts.offence_window.end}` : ''}` : 'not declared'}</span></div>
          <div className="text-muted-foreground">Stated claims ({stated.length}):</div>
          {stated.length ? (
            <ul className="pl-3 list-disc">
              {stated.map((c, i) => (
                <li key={i} data-testid="case-facts-claim">{claimText(c)} <span className="text-muted-foreground">[{c.source}]</span>{c.note ? <span className="text-muted-foreground"> · {c.note}</span> : null}</li>
              ))}
            </ul>
          ) : <p className="pl-3 text-muted-foreground">none</p>}
          {!canEdit && <p data-testid="case-facts-read-only" className="text-[10px] text-muted-foreground">{CASE_FACTS_READ_ONLY}</p>}
        </div>
      )}

      {state === 'ready' && editing && (
        <div className="mt-2 space-y-2" data-testid="case-facts-form">
          <div className="flex flex-wrap items-center gap-2">
            <label className="flex items-center gap-1">Kind
              <select data-testid="case-facts-kind-input" value={kind} onChange={(e) => setKind(e.target.value as CaseKind | '')} className={input}>
                <option value="">not declared</option>
                {KINDS.map((k) => <option key={k} value={k}>{KIND_LABEL[k]}</option>)}
              </select>
            </label>
            <label className="flex items-center gap-1">Offence from
              <input type="date" data-testid="case-facts-start-input" value={start} onChange={(e) => setStart(e.target.value)} className={input} />
            </label>
            <label className="flex items-center gap-1">to
              <input type="date" data-testid="case-facts-end-input" value={end} onChange={(e) => setEnd(e.target.value)} className={input} />
            </label>
          </div>
          <div className="space-y-1">
            {claims.map((c, i) => (
              <div key={i} className="flex flex-wrap items-center gap-1" data-testid="case-facts-claim-row">
                <select aria-label="Claim kind" value={c.kind} onChange={(e) => setClaim(i, { kind: e.target.value as StatedClaimKind })} className={input}>
                  {CLAIM_KINDS.map((k) => <option key={k} value={k}>{k.replace('_', ' ')}</option>)}
                </select>
                <input aria-label="Value" type="number" min={0} value={c.value} onChange={(e) => setClaim(i, { value: Number(e.target.value) })} className={`${input} w-24`} />
                <input aria-label="Unit" placeholder={c.kind === 'duration' ? 's, min, h, d' : 'unit'} value={c.unit} onChange={(e) => setClaim(i, { unit: e.target.value })} className={`${input} w-20`} />
                {c.kind === 'channel_total'
                  ? <input aria-label="Channel" placeholder="channel" value={c.channel || ''} onChange={(e) => setClaim(i, { channel: e.target.value })} className={`${input} w-32`} />
                  : <input aria-label="Path" placeholder="what it covers" value={c.path || ''} onChange={(e) => setClaim(i, { path: e.target.value })} className={`${input} w-32`} />}
                {c.kind === 'duration' && (
                  <>
                    <input aria-label="From transfer" placeholder="from tx hash" value={c.from_ref || ''} onChange={(e) => setClaim(i, { from_ref: e.target.value })} className={`${input} w-28`} />
                    <input aria-label="To transfer" placeholder="to tx hash" value={c.to_ref || ''} onChange={(e) => setClaim(i, { to_ref: e.target.value })} className={`${input} w-28`} />
                  </>
                )}
                <select aria-label="Source" value={c.source} onChange={(e) => setClaim(i, { source: e.target.value as StatedClaimSource })} className={input}>
                  {SOURCES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
                <input aria-label="Note" placeholder="note" value={c.note || ''} onChange={(e) => setClaim(i, { note: e.target.value })} className={`${input} w-32`} />
                <button aria-label="Remove claim" data-testid="case-facts-remove-claim" onClick={() => setClaims((cs) => cs.filter((_, j) => j !== i))}
                  className="p-1 rounded hover:bg-secondary/60"><Trash2 size={11} /></button>
              </div>
            ))}
            <button data-testid="case-facts-add-claim" onClick={() => setClaims((cs) => [...cs, blankClaim()])}
              className="flex items-center gap-1 px-2 py-0.5 rounded border border-border text-[11px] hover:bg-secondary/60">
              <Plus size={11} /> Add a stated claim
            </button>
          </div>
          {error && <p className="text-destructive" data-testid="case-facts-save-error">{error}</p>}
          <div className="flex gap-2">
            <button data-testid="case-facts-save" disabled={busy} onClick={save}
              className="px-2.5 py-1 rounded bg-primary text-primary-foreground text-[11px] font-medium disabled:opacity-50">Save</button>
            <button data-testid="case-facts-cancel" disabled={busy} onClick={() => { setEditing(false); setError(''); }}
              className="px-2.5 py-1 rounded border border-border text-[11px]">Cancel</button>
          </div>
        </div>
      )}
    </section>
  );
}
